import { useCallback, useEffect, useState } from 'react';
import {
  Crown,
  Loader2,
  Plus,
  Trash2,
  Lock,
  Clock,
  CheckCircle2,
  Search,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * VipManagerCard — admin-only self-serve manager for the lifetime-free VIP
 * whitelist. Combines env-baked (read-only) entries with admin-added (DB)
 * entries and shows signup status for each.
 *
 * Layout: the card itself only shows the header, stats strip, and the
 * add-VIP form so it stays compact on the Profile page. The full list is
 * moved behind a "View VIP List" button that opens a dialog with search +
 * per-row delete controls. This keeps the admin section from dominating
 * the Profile page while still giving one-click access when needed.
 */
export default function VipManagerCard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [filter, setFilter] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // holds an entry
  const [deleting, setDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/vip-lifetime');
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load VIP list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAdd = async (e) => {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter an email');
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post('/admin/vip-lifetime', {
        email: trimmed,
        note: note.trim() || undefined,
      });
      if (data.already_vip) {
        toast.info(`${trimmed} is already a VIP${data.source === 'env' ? ' (env-baked)' : ''}`);
      } else {
        toast.success(`${trimmed} added — lifetime Pro on next sign-in`);
      }
      setEmail('');
      setNote('');
      await fetchEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add VIP');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(
        `/admin/vip-lifetime/${encodeURIComponent(confirmDelete.email)}`,
      );
      toast.success(`${confirmDelete.email} removed from VIP list`);
      setConfirmDelete(null);
      await fetchEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove VIP');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = filter
    ? entries.filter((e) =>
        [e.email, e.note, e.added_by_email]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(filter.toLowerCase())),
      )
    : entries;

  const dbCount = entries.filter((e) => e.source === 'db').length;
  const envCount = entries.filter((e) => e.source === 'env').length;
  const signedUpCount = entries.filter((e) => e.signed_up).length;
  const pendingCount = entries.length - signedUpCount;

  return (
    <div
      className="mt-6 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-3 sm:p-4"
      data-testid="vip-manager-card"
    >
      <div className="flex items-start gap-2.5">
        <Crown className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">VIP Lifetime Access</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage the lifetime free-access list. Users on this list bypass billing
            entirely — their account reads as <span className="font-mono">plan: lifetime</span>{' '}
            with <span className="font-mono">is_pro: true</span> from the moment they sign in.
          </p>

          {/* Stats strip */}
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-background border border-border py-2">
              <div className="text-lg font-display font-semibold text-foreground">
                {loading ? '—' : entries.length}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                Total VIPs
              </div>
            </div>
            <div className="rounded-lg bg-background border border-border py-2">
              <div className="text-lg font-display font-semibold text-foreground">
                {loading ? '—' : signedUpCount}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                Signed up
              </div>
            </div>
            <div className="rounded-lg bg-background border border-border py-2">
              <div className="text-lg font-display font-semibold text-foreground">
                {loading ? '—' : `${envCount}/${dbCount}`}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                Env / DB
              </div>
            </div>
          </div>

          {/* Add form */}
          <form
            onSubmit={handleAdd}
            className="mt-4 flex flex-col sm:flex-row gap-2 items-stretch"
            data-testid="vip-add-form"
          >
            <Input
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder="dj@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={adding}
              className="h-9 text-sm rounded-lg flex-1"
              data-testid="vip-add-email-input"
            />
            <Input
              type="text"
              placeholder="Note (optional) — e.g. beta tester"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={adding}
              className="h-9 text-sm rounded-lg flex-1"
              data-testid="vip-add-note-input"
            />
            <Button
              type="submit"
              size="sm"
              disabled={adding || !email.trim()}
              className="h-9 rounded-lg text-xs"
              data-testid="vip-add-submit"
            >
              {adding ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Adding…
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add VIP
                </>
              )}
            </Button>
          </form>

          {/* View list trigger */}
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Pending</span> = user hasn&apos;t signed
              up yet · they&apos;ll auto-activate on registration.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setListOpen(true)}
              disabled={loading}
              className="h-8 rounded-lg text-xs shrink-0"
              data-testid="vip-view-list-button"
            >
              <Users className="w-3.5 h-3.5 mr-1.5" />
              View VIP list ({loading ? '…' : entries.length})
            </Button>
          </div>
        </div>
      </div>

      {/* Full list dialog */}
      <Dialog
        open={listOpen}
        onOpenChange={(v) => {
          setListOpen(v);
          if (!v) setFilter('');
        }}
      >
        <DialogContent
          className="max-w-lg p-0 gap-0 overflow-hidden"
          data-testid="vip-list-dialog"
        >
          <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Crown className="w-4 h-4 text-[hsl(var(--primary))]" />
              VIP Lifetime List
              <Badge
                variant="outline"
                className="ml-1 h-5 px-1.5 text-[10px] uppercase tracking-widest border-border text-muted-foreground"
              >
                {entries.length} total
              </Badge>
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              <span className="text-[hsl(var(--primary))] font-medium">{signedUpCount} active</span>{' '}
              · <span className="text-amber-500 font-medium">{pendingCount} pending signup</span>.
              Env-baked entries are locked; DB entries can be removed here at any time.
            </DialogDescription>
          </DialogHeader>

          {/* Filter */}
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Filter by email or note…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-8 pl-8 text-xs rounded-lg"
                data-testid="vip-filter-input"
                autoFocus
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-[60vh] overflow-y-auto px-4 pb-4">
            <div className="rounded-lg border border-border bg-background overflow-hidden">
              {loading ? (
                <div className="p-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading VIP list…
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">
                  {filter ? 'No VIPs match this filter.' : 'No VIPs yet — add one above.'}
                </div>
              ) : (
                <ul className="divide-y divide-border" data-testid="vip-list">
                  {filtered.map((v) => (
                    <li
                      key={`${v.source}:${v.email}`}
                      className="flex items-start gap-3 p-2.5 sm:p-3"
                      data-testid={`vip-row-${v.email}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-foreground truncate">
                            {v.email}
                          </span>
                          {v.signed_up ? (
                            <Badge
                              variant="outline"
                              className="h-4 px-1.5 text-[9px] uppercase tracking-widest border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]"
                            >
                              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Active
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="h-4 px-1.5 text-[9px] uppercase tracking-widest border-amber-500/40 text-amber-500"
                              title="Pending signup — auto-activates when this user registers"
                            >
                              <Clock className="w-2.5 h-2.5 mr-0.5" /> Pending
                            </Badge>
                          )}
                          {v.source === 'env' && (
                            <Badge
                              variant="outline"
                              className="h-4 px-1.5 text-[9px] uppercase tracking-widest border-border text-muted-foreground"
                              title="Env-baked entry — locked, contact engineer to remove"
                            >
                              <Lock className="w-2.5 h-2.5 mr-0.5" /> Env
                            </Badge>
                          )}
                        </div>
                        {v.note && (
                          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                            {v.note}
                          </div>
                        )}
                        {(v.added_by_email || v.added_at) && (
                          <div className="text-[10px] font-mono text-muted-foreground/70 mt-0.5">
                            {v.added_by_email && <>added by {v.added_by_email}</>}
                            {v.added_by_email && v.added_at && <> · </>}
                            {v.added_at && <>{new Date(v.added_at).toLocaleDateString()}</>}
                          </div>
                        )}
                      </div>
                      {v.source === 'db' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))] shrink-0"
                          onClick={() => setConfirmDelete(v)}
                          data-testid={`vip-remove-${v.email}`}
                          aria-label={`Remove ${v.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      ) : (
                        <div className="w-7" aria-hidden />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="vip-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove VIP access?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{confirmDelete?.email}</span> will
              lose lifetime access on their next sign-in. Their existing subscription state
              (trial, active Stripe, etc.) will still apply — this only removes the free-forever
              override.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] hover:bg-[hsl(var(--destructive))]/90"
              data-testid="vip-remove-confirm-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Removing…
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove VIP
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
